import ApexLogo from "@/assets/apex-logo.svg";
import { amplifyConfig } from "@/config/amplify";
import { useAuth } from "@/context/AuthContext";
import { Description } from "@mui/icons-material";
import { useEffect } from "react";
import { Location, useLocation, useNavigate } from "react-router-dom";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signInWithAzureAD, isAuthenticated, isLoading, error, user } = useAuth();

  const fromLocation = (location.state as { from?: Location })?.from;
  const from = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search}${fromLocation.hash}`
    : "/dashboard";

  // Get environment from config
  const environment = amplifyConfig.environment.toUpperCase();
  const showEnvBadge = ["TST", "DEV", "QA", "UAT"].includes(environment);

  // Check if we're coming back from OAuth redirect
  const isOAuthCallback =
    location.search.includes("code=") || location.hash.includes("access_token");

  useEffect(() => {
    if (isAuthenticated && user) {
      // User is authenticated, redirect to dashboard
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, user, navigate, from]);

  const handleAzureADSignIn = async () => {
    try {
      await signInWithAzureAD();
    } catch (err) {
      // Error is handled in AuthContext and displayed in the UI
    }
  };

  if (isLoading || (isOAuthCallback && !error)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-light dark:bg-background-dark">
        <div className="text-center">
          <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="text-subtext-light dark:text-subtext-dark">
            {isOAuthCallback ? "Completing sign in..." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-background-light p-4 dark:bg-background-dark">
      <div className="w-full max-w-md">
        {/* Container Card */}
        <div className="flex w-full flex-col items-center rounded-xl border border-border-light bg-card-light p-8 shadow-sm dark:border-border-dark dark:bg-card-dark">
          {/* Logo */}
          <div className="mb-6 flex flex-col items-center gap-4">
            <img
              src={ApexLogo}
              alt="Apex - Advanced Planning & Execution"
              className="h-20 w-auto"
            />
            <div className="flex items-center gap-2">
              <Description className="text-primary" sx={{ fontSize: 28 }} />
              <h2 className="text-xl font-bold tracking-tight text-text-light dark:text-text-dark">
                Apex IntelliDoc
              </h2>
              {showEnvBadge && (
                <span className="ml-2 rounded-md bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  {environment}
                </span>
              )}
            </div>
          </div>

          {/* Page Heading */}
          <div className="mb-6 w-full text-center">
            <h1 className="text-xl font-bold leading-tight tracking-tight text-text-light dark:text-text-dark">
              Sign in to your account
            </h1>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 w-full rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          {/* SSO Button */}
          <button
            onClick={handleAzureADSignIn}
            disabled={isLoading}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-border-light bg-card-light px-4 py-2 text-sm font-semibold text-text-light transition-colors hover:bg-background-light focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-border-dark dark:bg-card-dark dark:text-text-dark dark:hover:bg-background-dark dark:focus:ring-offset-card-dark"
            type="button"
          >
            <svg className="h-5 w-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1h9v9H1z" fill="#f25022"></path>
              <path d="M1 11h9v9H1z" fill="#00a4ef"></path>
              <path d="M11 1h9v9h-9z" fill="#7fba00"></path>
              <path d="M11 11h9v9h-9z" fill="#ffb900"></path>
            </svg>
            Sign in with Azure AD
          </button>

          {/* Divider */}
          <div className="my-6 flex w-full items-center gap-4">
            <hr className="w-full border-border-light dark:border-border-dark" />
            <span className="text-xs font-medium text-subtext-light dark:text-subtext-dark">
              OR
            </span>
            <hr className="w-full border-border-light dark:border-border-dark" />
          </div>

          {/* Email/Password Form (Placeholder) */}
          <form className="flex w-full flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
            {/* Email Field */}
            <div className="flex flex-col gap-1.5">
              <label
                className="text-sm font-medium text-text-light dark:text-text-dark"
                htmlFor="email"
              >
                Email Address
              </label>
              <div className="relative">
                <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtext-light dark:text-subtext-dark">
                  mail
                </span>
                <input
                  className="h-11 w-full rounded-lg border border-border-light bg-input-bg-light py-2 pl-10 pr-3 text-text-light placeholder:text-subtext-light focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-border-dark dark:bg-input-bg-dark dark:text-text-dark dark:placeholder:text-subtext-dark"
                  id="email"
                  placeholder="Enter your email"
                  type="email"
                  disabled
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="flex flex-col gap-1.5">
              <label
                className="text-sm font-medium text-text-light dark:text-text-dark"
                htmlFor="password"
              >
                Password
              </label>
              <div className="relative">
                <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtext-light dark:text-subtext-dark">
                  lock
                </span>
                <input
                  className="h-11 w-full rounded-lg border border-border-light bg-input-bg-light py-2 pl-10 pr-10 text-text-light placeholder:text-subtext-light focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-border-dark dark:bg-input-bg-dark dark:text-text-dark dark:placeholder:text-subtext-dark"
                  id="password"
                  placeholder="Enter your password"
                  type="password"
                  disabled
                />
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <a className="text-sm font-medium text-primary hover:underline" href="#">
                Forgot Password?
              </a>
            </div>

            {/* Login Button */}
            <button
              className="flex h-11 w-full items-center justify-center rounded-lg bg-slate-300 px-4 py-2 text-sm font-semibold text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-600"
              type="submit"
              disabled
            >
              Email/Password Login Not Available
            </button>
          </form>

          {/* Info Message */}
          <div className="mt-6 w-full rounded-lg border border-blue-200 bg-blue-50 p-3 text-center text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
            <span className="font-semibold">Demo Mode:</span> Use Azure AD single sign-on to
            authenticate.
          </div>
        </div>
      </div>
    </div>
  );
}
